import React, { useEffect, useState } from "react";
import { Table, Button, Spinner, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../../../layouts/default";
import { getCampaignDataIssues } from "../../../../store/slices/campaigns/dataIssue";
import { DateTime } from "luxon";
import SmartPagination from "../DynamicSmartPagination";
import JsonModal from "../JsonModel";

const RECORDS_PER_PAGE = 10;

const CampaignDataIssue = () => {
    const { campaignId, campaignUuid, type } = useParams();
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const [selectedPayload, setSelectedPayload] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const { dataIssueLogs } = useSelector((state) => state.dataIssueSlice);
    const { dataIssues = [], totalCount = 0, dataIssueLoading } = dataIssueLogs;

    const totalPages = Math.ceil(totalCount / RECORDS_PER_PAGE);

    useEffect(() => {
        if (campaignId) {
            dispatch(
                getCampaignDataIssues({
                    campaignId,
                    campaignUuid,
                    page: currentPage,
                    limit: RECORDS_PER_PAGE,
                    eventType: type || "", // apply type filter from params
                })
            );
        }
    }, [campaignId, currentPage, type, dispatch]);

    const handleExportCSV = () => {
        const headers = ["Value", "Reason", "Date Time", "CampaignId", "CampaignUuid"];
        const rows = dataIssues.map((issue) => [
            issue.value || issue.email || issue.email == null ? 'Null' : "",
            issue.eventType || "",
            issue.dateTime
                ? DateTime.fromISO(issue.dateTime).setZone("Asia/Kolkata").toFormat("dd MMM yyyy, hh:mm a")
                : "",

            campaignId,
            campaignUuid,
        ]);

        const csvContent =
            [headers, ...rows]
                .map((e) =>
                    e.map((cell) =>
                        `"${String(cell).replace(/"/g, '""')}"` // escape quotes
                    ).join(",")
                )
                .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Campaign_Data_Issues_${campaignId}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const openJsonModal = (payload) => {
        setSelectedPayload(payload);
        setShowModal(true);
    };

    const closeModal = () => setShowModal(false);

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <Layout>
            <div className="pt-3">
                <Button variant="secondary" className="mb-3" onClick={handleBack}>
                    ← Back
                </Button>
                <Row className="align-items-center mb-4">
                    <Col>
                        <h4 className="mb-2 text-primary">Data Issues</h4>
                        <p className="mb-4 text-muted">
                            <strong>Campaign UUID:</strong> {campaignUuid} <br />
                            <strong>Campaign ID:</strong> {campaignId} <br />
                            {type && (
                                <>
                                    <strong>Filter Type:</strong> {type}
                                </>
                            )}
                        </p>
                    </Col>
                    <Col className="text-end">
                        <Button variant="primary" onClick={handleExportCSV}>
                            Export CSV
                        </Button>
                    </Col>
                </Row>

                {dataIssueLoading ? (
                    <div className="text-center my-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <>
                        <Table striped bordered hover responsive className="shadow-sm rounded">
                            <thead className="table-secondary">
                                <tr>
                                    <th>#</th>
                                    <th>Email</th>
                                    <th>Reason</th>
                                    <th>Date Time</th>
                                    <th>Raw JSON</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(dataIssues) && dataIssues.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center text-muted">
                                            No Data Issues Found
                                        </td>
                                    </tr>
                                ) : (
                                    dataIssues.map((issue, index) => (
                                        <tr key={index}>
                                            <td>
                                                {(currentPage - 1) * RECORDS_PER_PAGE + index + 1}
                                            </td>
                                            <td>
                                                {(() => {
                                                    const rawEmail = issue?.rawPayload?.email;

                                                    if (!rawEmail || (typeof rawEmail === "string" && rawEmail.trim() === "")) {
                                                        // Case: null, undefined, or empty string
                                                        return <i className="text-muted">{issue?.email || "Null"}</i>;
                                                    }

                                                    if (Array.isArray(rawEmail)) {
                                                        // Case: array of emails
                                                        return (
                                                            <i className="text-muted">
                                                                {rawEmail.length > 0 ? rawEmail.join(", ") : issue?.email || "Null"}
                                                            </i>
                                                        );
                                                    }

                                                    // Case: string with value
                                                    return <i className="text-muted">{rawEmail || issue?.email || "NA"}</i>;
                                                })()}
                                            </td>

                                            <td
                                                className={
                                                    ["Bounce", "Complaint", "Reject"].includes(issue.eventType)
                                                        ? "text-danger fw-bold"
                                                        : ""
                                                }
                                            >
                                                {issue.eventType || "-"}
                                            </td>
                                            <td>
                                                {(() => {
                                                    let ts;

                                                    if (issue?.eventType === "Bounce") {
                                                        ts = issue?.rawPayload?.rawPayload?.bounce?.timestamp;
                                                    } else if (issue?.eventType === "Complaint") {
                                                        ts = issue?.rawPayload?.rawPayload?.complaint?.timestamp;
                                                    } else if (issue?.eventType === "Reject") {
                                                        ts = issue?.rawPayload?.rawPayload?.reject?.timestamp;
                                                    } else {
                                                        ts = issue?.rawPayload?.rawPayload?.timestamp;
                                                    }

                                                    if (ts) {
                                                        return DateTime.fromISO(ts)
                                                            .setZone("Asia/Kolkata")
                                                            .toFormat("dd MMM yyyy, hh:mm a");
                                                    }
                                                    else if (issue?.dateTime) {
                                                        return DateTime.fromISO(issue.dateTime)
                                                            .setZone("Asia/Kolkata")
                                                            .toFormat("dd MMM yyyy, hh:mm a");
                                                    } else {
                                                        return "-";
                                                    }
                                                })()}
                                            </td>

                                            <td>
                                                <Button
                                                    variant="outline-dark"
                                                    size="sm"
                                                    onClick={() => openJsonModal(issue.rawPayload || issue)}
                                                >
                                                    View JSON
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>

                        <div className="text-center mt-3 text-muted">
                            Showing {Array.isArray(dataIssues) && dataIssues?.length} logs of {totalCount} total. Page {currentPage} of {totalPages}.
                        </div>

                        <SmartPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={(page) => setCurrentPage(page)}
                        />

                        <JsonModal
                            show={showModal}
                            onClose={closeModal}
                            payload={selectedPayload}
                        />
                    </>
                )}
            </div>
        </Layout>
    );
};

export default CampaignDataIssue;
